import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Typography, useTheme } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { SignedIn, SignedOut, SignIn, useAuth } from '@clerk/clerk-react';

import VideoLayout from '../components/layout/VideoLayout';
import { SessionService } from '../services/SessionService';
import { InvalidSessionException } from '../services/Exceptions';
import sessionStore from '../stores/SessionStore';

function LoginClerk() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useAuth();
  const attemptedBootstrapRef = useRef(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  const redirectTo = useMemo(() => {
    const { state } = location;
    if (!state || typeof state !== 'object') {
      return '/dashboard';
    }
    const { from } = state as { from?: unknown };
    if (!from || typeof from !== 'object') {
      return '/dashboard';
    }

    const { pathname } = from as { pathname?: unknown };
    return typeof pathname === 'string' ? pathname : '/dashboard';
  }, [location.state]);

  useEffect(() => {
    if (sessionStore.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, sessionStore.isAuthenticated]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (sessionStore.isAuthenticated) {
      return;
    }

    if (!isSignedIn) {
      return;
    }

    if (attemptedBootstrapRef.current) {
      return;
    }

    if (isBootstrapping) {
      return;
    }

    const bootstrap = async (): Promise<void> => {
      setIsBootstrapping(true);
      attemptedBootstrapRef.current = true;
      setBootstrapError(null);
      try {
        await SessionService.createSessionFromClerkCookie();
        const validateSessionResponse = await SessionService.validateSession();
        if (validateSessionResponse?.data?.user) {
          sessionStore.setUser(validateSessionResponse.data.user);
          sessionStore.setIsAuthenticated(true);
          navigate(redirectTo, { replace: true });
        } else {
          setBootstrapError('Signed in with Clerk, but no Django session was created yet.');
        }
      } catch (error: unknown) {
        if (error instanceof InvalidSessionException && error.status === 401) {
          setBootstrapError(
            'Clerk says you are signed in, but the backend rejected /auth/session (401). This usually means the Clerk __session cookie is not being sent to the Django API.'
          );
          return;
        }

        if (error instanceof Error && error.message) {
          setBootstrapError(error.message);
          return;
        }

        setBootstrapError('Failed to create a Django session from Clerk.');
      } finally {
        setIsBootstrapping(false);
      }
    };

    bootstrap().catch(() => undefined);
  }, [isLoaded, isSignedIn, isBootstrapping, navigate, redirectTo]);

  return (
    <VideoLayout>
      <Box sx={{ maxWidth: '28rem' }}>
        <Typography
          level='h2'
          sx={{
            fontSize: '2.5rem',
            lineHeight: '3rem',
            fontWeight: 600,
            marginBottom: '1.5rem',
            fontFamily: theme.fontFamily.display
          }}
        >
          {t('login_page.sign_in_to_your_account')}
        </Typography>
        <SignedOut>
          <SignIn
            routing='path'
            path='/login'
            signUpUrl='/signup'
            fallbackRedirectUrl='/dashboard'
            signUpFallbackRedirectUrl='/dashboard'
          />
        </SignedOut>
        <SignedIn>
          {isBootstrapping && <Typography level='body-md'>Signing you in...</Typography>}
          {bootstrapError ? <Typography level='body-sm'>{bootstrapError}</Typography> : null}
        </SignedIn>
      </Box>
    </VideoLayout>
  );
}

export default observer(LoginClerk);
